/**
 * Time and timezone utility functions
 */
import { DateTime } from 'luxon';
import { TIMEZONE_MAPPING, } from '../constants/timezone-constants.js';
const TIMEZONE_ENTRIES = Object.entries(TIMEZONE_MAPPING);
const IANA_TO_TIMEZONE_ENUM = TIMEZONE_ENTRIES.reduce((acc, [timezoneEnumValue, ianaTimezone]) => {
    acc[ianaTimezone.toLowerCase()] = timezoneEnumValue;
    return acc;
}, {});
/**
 * Converts timezone values into enum-style keys used by the selector/API.
 * Accepts either enum values (e.g., `ASIA_KOLKATA`) or IANA identifiers
 * (e.g., `Asia/Kolkata`).
 */
export function toTimezoneEnumValue(timezoneName) {
    if (!timezoneName) {
        return undefined;
    }
    const trimmedTimezone = timezoneName.trim();
    if (!trimmedTimezone) {
        return undefined;
    }
    if (Object.prototype.hasOwnProperty.call(TIMEZONE_MAPPING, trimmedTimezone)) {
        return trimmedTimezone;
    }
    return IANA_TO_TIMEZONE_ENUM[trimmedTimezone.toLowerCase()];
}
/**
 * Converts a timezone enum value to IANA timezone identifier
 * @param timezoneName Timezone value (can be enum format like 'ASIA_KOLKATA' or IANA format like 'Asia/Kolkata')
 * @returns IANA timezone identifier, or undefined if invalid or unrecognized
 *
 * @example
 * toIANATimezone('ASIA_KOLKATA') // Returns: 'Asia/Kolkata'
 * toIANATimezone('Asia/Kolkata') // Returns: 'Asia/Kolkata' (mapped via enum lookup)
 * toIANATimezone('America/New_York') // Returns: 'America/New_York' (valid IANA, passed through)
 * toIANATimezone('Mars/Olympus_Mons') // Returns: undefined (invalid)
 * toIANATimezone(undefined) // Returns: undefined
 */
export function toIANATimezone(timezoneName) {
    const timezoneEnumValue = toTimezoneEnumValue(timezoneName);
    if (timezoneEnumValue) {
        return TIMEZONE_MAPPING[timezoneEnumValue];
    }
    if (!timezoneName) {
        return undefined;
    }
    // Keep support for valid IANA values outside our known map
    const trimmedTimezone = timezoneName.trim();
    if (!trimmedTimezone) {
        return undefined;
    }
    try {
        Intl.DateTimeFormat('en-US', { timeZone: trimmedTimezone });
        return trimmedTimezone;
    }
    catch {
        // Unknown timezone - return undefined
        return undefined;
    }
}
/**
 * Format a date in a specific timezone
 * Falls back to default locale string if timezone is invalid or not provided
 *
 * @param date - The date to format
 * @param timeZone - IANA timezone string (e.g., 'America/New_York', 'UTC')
 * @returns Formatted date string in the specified timezone
 *
 * @example
 * formatInTimeZone(new Date(), 'America/New_York')
 * // Returns: "1/15/2025, 3:30:45 PM"
 *
 * formatInTimeZone(new Date(), 'UTC')
 * // Returns: "1/15/2025, 8:30:45 PM"
 */
export function formatInTimeZone(date, timeZone) {
    try {
        if (timeZone) {
            return date.toLocaleString('en-US', { timeZone });
        }
        return date.toLocaleString();
    }
    catch (error) {
        // Invalid timezone - fall back to default locale string
        console.warn('Error formatting date in timezone, using default', {
            timeZone,
            error: error instanceof Error ? error.message : String(error),
        });
        return date.toLocaleString();
    }
}
/**
 * Get current timestamp as ISO string in the specified timezone
 * Falls back to UTC if timezone is not provided or invalid
 *
 * @param timezone IANA timezone identifier (e.g., 'America/New_York')
 * @returns ISO formatted date string (YYYY-MM-DDTHH:mm:ss)
 *
 * @example
 * formatISODateInTimezone('America/New_York')
 * // Returns: "2025-01-15T15:30:45"
 */
export function formatISODateInTimezone(timezoneName) {
    if (!timezoneName) {
        return (DateTime.utc().toISO({ includeOffset: false }) ?? new Date().toISOString());
    }
    // Convert enum-style timezone to IANA format if needed
    const ianaTimezone = toIANATimezone(timezoneName);
    if (!ianaTimezone) {
        // Invalid timezone, fall back to UTC
        console.warn(`Invalid timezone: ${timezoneName}, falling back to UTC`);
        return (DateTime.utc().toISO({ includeOffset: false }) ?? new Date().toISOString());
    }
    const dt = DateTime.now().setZone(ianaTimezone);
    if (!dt.isValid) {
        // Invalid timezone, fall back to UTC
        return (DateTime.utc().toISO({ includeOffset: false }) ?? new Date().toISOString());
    }
    return dt.toISO({ includeOffset: false }) ?? new Date().toISOString();
}
/**
 * Format a date as a relative time string (e.g., "2m ago", "3h ago", "5d ago")
 *
 * @param date - The date to format
 * @returns Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 120000))
 * // Returns: "2m ago"
 *
 * formatRelativeTime(new Date(Date.now() - 7200000))
 * // Returns: "2h ago"
 */
export function formatRelativeTime(date) {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1)
        return 'just now';
    if (diffMins < 60)
        return `${diffMins}m ago`;
    if (diffHours < 24)
        return `${diffHours}h ago`;
    return `${diffDays}d ago`;
}
//# sourceMappingURL=time-utils.js.map